import {
  useState, useEffect, forwardRef, useReducer, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import { Checkbox } from '@headlessui/react';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';

const List = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const [keySlug] = useState(item.id);

  const extractDataFromUrl = (values) => (values?.split(',') || []).reduce((acc, v) => {
    if (item?.values.find((iv) => iv.slug === v)) acc.push(v);
    return acc;
  }, []);

  const [currentValue, setCurrentValue] = useReducer((prev, { values, runSearch }) => {
    let newValue = {
      ...prev, validValues: [...prev.validValues], isChange: nanoid(), keyList: nanoid(),
    };
    if (values) {
      newValue = { ...newValue, values };
    }
    if (runSearch) {
      newValue.validValues = [...newValue.values];
      newValue.runSearch = nanoid();
    }
    if (onFilter) {
      newValue = { ...newValue, validValues: [...newValue.values], runSearch: null };
    }
    return { ...newValue };
  }, {
    values: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    validValues: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    isChange: null,
    keyList: nanoid(),
  });

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (!onScreen) return prev;

    /*
    if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      onValid(tmpValue);
      tmpValue = false;
    } else if (!tmpValue) {
      if (onValid && !currentValue.runSearch) onValid();
    }
    */
    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid && !currentValue.runSearch) tmpValue = 'willCloseAndValid';
    }

    return tmpValue;
  }, false);

  function clearValue(runSearch, e) {
    if (e?.stopPropagation) e.stopPropagation();
    setCurrentValue({ values: [], runSearch });
    setIsOpenModal(false);
  }

  function getValue(out) {
    if (currentValue?.validValues?.length) {
      if (out === 'url') return currentValue.validValues.join(',');
      return currentValue.validValues.map((v) => (
        !Number.isNaN(parseFloat(v)) ? parseFloat(v) : v
      ));
    }
    return null;
  }

  function getOutput() {
    return {
      keySlug,
      slug: item.slug,
      url: getValue('url'),
      values: getValue(),
      graphql: {
        [item.slug]: getValue(),
      },
    };
  }

  function isSameDefault() {
    return !currentValue.validValues.length;
  }

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.runSearch) {
      if (onValid) onValid();
      /* useSearchZustandStore.setState({filterEvent: {
      action:'itemfilterlistchange',
      slug: slugEvent}})
      */
    }
  }, [currentValue.runSearch]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    }
  }, [isOpenModal]);

  function onChange(slug, checked) {
    if (item.onlyChoice) {
      if ((item?.skin === 'button' && currentValue.values?.[0] === slug)
        || (item?.skin !== 'button' && !checked)) {
        return setCurrentValue({ values: [], runSearch: null });
      }
      return setCurrentValue({ values: [slug], runSearch: null });
    }
    if ((item?.skin === 'button'
      && !currentValue.values.find((v) => v === slug))
      || checked) {
      currentValue.values.push(slug);
    } else {
      currentValue.values = currentValue.values.filter((v) => v !== slug);
    }

    return setCurrentValue({ values: [...currentValue.values], runSearch: null });
  }

  function closeOnlyNotSaveValues(action) {
    setCurrentValue({ values: currentValue.validValues, runSearch: null });
    setIsOpenModal(action);
  }

  function callrunSearch() {
    setCurrentValue({ runSearch: true });
    setIsOpenModal(false);
  }

  function renderTitle() {
    if (currentValue?.validValues?.length) {
      return (
        <div className="text-sm flex justify-center items-center transition text-white py-[8px] px-[8px] bg-purple rounded-lg cursor-pointer hover:bg-purple/80">
        {!item?.noicon && <Icon className="mr-2 text-white size-4 group-hover:text-white" name={item.slug} />}
          <div
            className="grid grid-flow-col auto-cols-max gap-x-2"
            onClick={() => {
              setIsOpenModal(true);
            }}
          >
            {currentValue?.validValues?.map((v) => {
              const choice = item.values.find((vv) => vv.slug === v);
              return (
                <p className="last:border-l border-white/30 last:pl-2" key={nanoid()}>
                  <span className="py-[2px] px-2bg-opacity-70">{t(choice.title)}</span>
                </p>
              );
            })}
          </div>
          <button className="w-3" onClick={(e) => { clearValue(true, e); }}><Icon name="close" className="mx-1 w-full text-white fill-current size-1.5" /></button>
        </div>
      );
    }
    return (
      <p className="text-sm text-white flex justify-center whitespace-nowrap w-full items-center transition-all duration-200 py-[8px] px-[8px] rounded-lg cursor-pointer bg-purple-6 hover:bg-purple/70 group" onClick={() => setIsOpenModal(true)}>
        {!item?.noicon && <Icon className="mr-2 transition-colors duration-200 size-4 text-purple-4/70 group-hover:text-white" name={item.slug} />}
        <span className="transition-colors duration-200">{t(item.title)}</span>
        <Icon name="next" className="ml-2 w-2 h-2 transition-colors duration-200 transform rotate-90 text-purple-1 group-hover:text-white" />
      </p>
    );
  }

  function renderContent() {
    return (
      <ul className={`gap-y-2 whitespace-nowrap flex-wrap flex gap-x-2 auto-cols-auto ${item.skin === 'button' ? '' : 'flex-col'}`} key={currentValue.keyList}>
        {(item?.values || []).map(({ slug, title, icon }) => (
          <li key={`searchfilteritemlist${item.slug || nanoid()}${slug || nanoid()}`}>
            {item?.skin === 'button'
              ? (
                <button className={`min-w-20 group flex flex-col justify-center items-center px-3 py-2 rounded-lg ${(currentValue.values.includes(slug) ? 'text-purple bg-blues-light' : 'bg-purple-0 hover:bg-purple-0')}`} onClick={(e) => onChange(slug, e)}>
                  {!icon || <Icon className={`fill-current w-7 h-7 mt-1 ${(currentValue.values.includes(slug) ? 'text-purple' : 'text-dark')}`} name={icon} />}
                  <span className="mt-2">{t(title)}</span>
                </button>
              )
              : (
                <label className="inline-flex items-center cursor-pointer">
                  <Checkbox
                    checked={currentValue.values.includes(slug)}
                    onChange={(checked) => onChange(slug, checked)}
                    className={`size-5 inline-block items-center justify-center mr-2 rounded border-purple-4/20 hover:border-purple-5 focus:ring-0 focus:ring-offset-0 ${
                      currentValue.values.includes(slug) ? 'bg-blue border-blue' : 'bg-purple-4/20'
                    }`}
                  >
                    {({ checked }) => (
                      <svg
                        className={`p-0.5 size-5 text-white ${checked ? 'block' : 'hidden'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </Checkbox>
                  {!icon || <Icon className={`fill-current text-purple-5/50 w-5 h-5 mr-2 ${item?.values?.[0].icon === 'flash' ? 'text-purple' : ''} ${item?.values?.[0].icon === 'superhost' ? 'text-purple' : ''}`} name={icon} />}
                  <span className="text-sm cursor-pointer text-purple-6" onClick={() => onChange(slug, !currentValue.values.includes(slug))}>{t(title)}</span>
                </label>
              )
            }
          </li>
        ))}
      </ul>
    );
  }
  function renderModal() {
    return (
      <>
        <div
          className="absolute top-0 left-0 z-30 w-screen h-screen bg-purple-6/50"
          onClick={() => {
            closeOnlyNotSaveValues('outside');
          }}
        />
        <div className="-mt-[38px]">
          <div className="flex flex-col min-w-[250px] max-h-[85vh] rounded-lg bg-white z-40 absolute px-6 pt-6 pb-4 shadow-button">
            <h1 className="flex-none pr-8 mb-4 font-medium text-purple-6">{t(item.title)}</h1>
            <button
              className="absolute top-4 right-4 size-5"
              onClick={() => {
                closeOnlyNotSaveValues('iconclose');
              }}
            >
              <Icon name="close" className="mx-1 fill-current size-4 text-purple-6/30 hover:text-black" />
            </button>
            <div className="overflow-y-auto flex-1 min-h-0 pr-2 -mr-2">
              {renderContent()}
            </div>
            <div className="flex flex-none justify-between items-center mt-4 mb-2">
              <button className="text-base rounded-none border-b text-purple-5/50 border-purple-5/50 hover:text-purple hover:border-purple" onClick={(e) => clearValue(true, e)}>{t('search:clean')}</button>
              <div>
                {renderButtonStartTour?.()}
                <button className="px-6 py-2 purple-gradient" onClick={() => callrunSearch()}>{t('search:save')}</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderFilter() {
    if (!item?.title) return null;
    return (
      <div>
        <p className="pb-2 font-medium text-black">{t(item.title)}</p>
        {renderContent()}
      </div>
    );
  }
  return (
    <>
      {!(onScreen && isOpenModal !== true) || renderTitle()}
      {!(onScreen && isOpenModal === true) || (
      <>
        {renderTitle()}
        {renderModal()}
      </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default List;
